import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { mockGoodsReceiptService } from '@/services/mock/MockGoodsReceiptService';
import { mockDocumentCommentService } from '@/services/mock/MockDocumentCommentService';
import { GoodsReceiptRecord } from '@/types/domain/grn-management';
import { DocumentAttachment, RequestComment } from '@/types/domain';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { formatDate } from '@/lib/utils';
import { animateFadeIn } from '@/lib/motion';
import { ROUTES } from '@/constants/routes';
import { usePermission } from '@/hooks/usePermission';
import { getAvailableGrnActions } from '@/features/grn/utils/grnTransitions';
import {
  ArrowLeft,
  PackageCheck,
  ShieldCheck,
  Database,
  Layers,
  FileText,
  MessageSquare,
  History,
  Printer,
  XCircle,
  CheckCircle2,
  AlertTriangle,
  ExternalLink,
  Receipt,
} from 'lucide-react';
import { DocumentManagementPanel } from '@/components/data-display/DocumentManagementPanel';
import { EnterpriseCommentThread } from '@/components/data-display/EnterpriseCommentThread';
import { GrnInspectionModal } from '@/features/grn/components/GrnInspectionModal';
import { GrnPrintSlipModal } from '@/features/grn/components/GrnPrintSlipModal';
import { GrnAuditHistoryTab } from '@/features/grn/components/GrnAuditHistoryTab';
import { GrnSapSyncModal } from '@/features/grn/components/GrnSapSyncModal';

type GrnDetailTab = 'OVERVIEW' | 'ITEMS' | 'QUALITY' | 'MATCH' | 'SAP' | 'DOCUMENTS' | 'COMMENTS' | 'AUDIT';

export interface GrnDetailPageProps {
  recordId?: string;
  isDrawer?: boolean;
  onClose?: () => void;
}

export const GrnDetailPage: React.FC<GrnDetailPageProps> = ({
  recordId,
  isDrawer,
}) => {
  const { id: paramId } = useParams<{ id: string }>();
  const id = recordId || paramId;
  const navigate = useNavigate();
  const pageRef = useRef<HTMLDivElement>(null);
  const { can, user } = usePermission();

  const [grn, setGrn] = useState<GoodsReceiptRecord | null>(null);
  const [documents, setDocuments] = useState<DocumentAttachment[]>([]);
  const [comments, setComments] = useState<RequestComment[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Modals state
  const [isInspectionModalOpen, setIsInspectionModalOpen] = useState(false);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [isSapModalOpen, setIsSapModalOpen] = useState(false);
  const [isSyncingSap, setIsSyncingSap] = useState(false);

  const [activeTab, setActiveTab] = useState<GrnDetailTab>('OVERVIEW');

  const loadData = async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      const [record, docs, comms] = await Promise.all([
        mockGoodsReceiptService.getGoodsReceiptById(id),
        mockDocumentCommentService.getDocuments('GRN', id),
        mockDocumentCommentService.getComments('GRN', id),
      ]);

      setGrn(record);
      setDocuments(docs);
      setComments(comms);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    animateFadeIn(pageRef.current);
    loadData();
  }, [id]);

  const handleConfirmSapSync = async () => {
    if (!grn) return;
    try {
      setIsSyncingSap(true);
      await mockGoodsReceiptService.syncGrnWithSap(grn.id);
      setIsSapModalOpen(false);
      await loadData();
    } finally {
      setIsSyncingSap(false);
    }
  };

  const handleAcceptAll = async () => {
    if (!grn) return;
    await mockGoodsReceiptService.acceptAllGoods(grn.id);
    await loadData();
  };

  const handleQualityHold = async () => {
    if (!grn) return;
    await mockGoodsReceiptService.placeOnQualityHold(grn.id, 'Quarantine hold applied by warehouse quality lead.');
    await loadData();
  };

  const handleReleaseHold = async () => {
    if (!grn) return;
    await mockGoodsReceiptService.releaseFromQualityHold(grn.id, 'Released from quarantine after secondary laboratory re-test.');
    await loadData();
  };

  const handleCancelGrn = async () => {
    if (!grn) return;
    await mockGoodsReceiptService.cancelGrn(grn.id, 'Administrative cancellation requested by procurement manager.');
    await loadData();
  };

  if (isLoading) {
    return (
      <div className="p-16 text-center text-xs text-foreground-secondary font-medium font-poppins">
        <PackageCheck className="h-8 w-8 animate-bounce mx-auto text-primary mb-2" />
        Loading Goods Receipt Command Center...
      </div>
    );
  }

  if (!grn) {
    return (
      <div className="p-16 text-center space-y-3 font-poppins">
        <h2 className="text-lg font-extrabold text-foreground">Goods Receipt Not Found</h2>
        <p className="text-xs text-foreground-secondary">The requested GRN does not exist or has been archived.</p>
        <Button onClick={() => navigate(ROUTES.GRN.LIST)} className="rounded-xl font-bold font-poppins cursor-pointer">
          Return to GRN Control Center
        </Button>
      </div>
    );
  }

  const availableActions = getAvailableGrnActions(grn, { can, role: user?.role });
  const isInspectionAvailable = availableActions.find((a) => a.action === 'RECORD_INSPECTION')?.isAvailable;
  const isAcceptAllAvailable = availableActions.find((a) => a.action === 'ACCEPT_ALL')?.isAvailable;
  const isPlaceHoldAvailable = availableActions.find((a) => a.action === 'PLACE_ON_HOLD')?.isAvailable;
  const isReleaseHoldAvailable = availableActions.find((a) => a.action === 'RELEASE_FROM_HOLD')?.isAvailable;
  const isSyncSapAvailable = availableActions.find((a) => a.action === 'SYNC_SAP_MIGO')?.isAvailable;
  const isCancelAvailable = availableActions.find((a) => a.action === 'CANCEL')?.isAvailable;

  return (
    <div ref={pageRef} className={`space-y-4 font-poppins ${isDrawer ? 'pb-2' : 'pb-24 max-w-7xl mx-auto'}`}>
      {/* Top Header & Navigation */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {!isDrawer && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(ROUTES.GRN.LIST)}
              className="h-9 rounded-xl border-border font-bold text-xs font-poppins cursor-pointer"
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Goods Receipts
            </Button>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xl font-black text-foreground tracking-tight font-poppins">{grn.grnNumber}</span>
            <span className="text-xs font-mono font-bold text-slate-500 bg-surface px-2 py-0.5 rounded border border-border">
              Rev {grn.version}
            </span>
            {grn.sapGrnNumber && (
              <span className="font-mono text-xs font-bold text-purple-600 bg-purple-500/10 px-2 py-0.5 rounded border border-purple-500/20 flex items-center gap-1">
                <Database className="h-3 w-3" /> SAP MIGO: {grn.sapGrnNumber}
              </span>
            )}
            {grn.status === 'QUALITY_HOLD' && (
              <span className="text-xs font-bold text-purple-600 bg-purple-500/10 px-2 py-0.5 rounded border border-purple-500/20 flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5" /> Quality Quarantine Hold
              </span>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsPrintModalOpen(true)}
            className="rounded-xl font-bold border-border text-xs font-poppins cursor-pointer"
          >
            <Printer className="h-3.5 w-3.5 mr-1.5" /> Print / Export GRN Slip
          </Button>

          {isInspectionAvailable && (
            <Button
              size="sm"
              onClick={() => setIsInspectionModalOpen(true)}
              className="rounded-xl font-bold bg-primary hover:bg-primary/90 text-white shadow-2xs text-xs"
            >
              <ShieldCheck className="h-3.5 w-3.5 mr-1.5" /> Record Inspection
            </Button>
          )}

          {isAcceptAllAvailable && (
            <Button
              size="sm"
              onClick={handleAcceptAll}
              className="rounded-xl font-bold bg-emerald-600 hover:bg-emerald-700 text-white shadow-2xs text-xs"
            >
              <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Accept All Goods
            </Button>
          )}

          {isPlaceHoldAvailable && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleQualityHold}
              className="rounded-xl font-bold border-amber-500/40 text-amber-700 hover:bg-amber-50 text-xs"
            >
              <AlertTriangle className="h-3.5 w-3.5 mr-1.5 text-amber-600" /> Place on Hold
            </Button>
          )}

          {isReleaseHoldAvailable && (
            <Button
              size="sm"
              onClick={handleReleaseHold}
              className="rounded-xl font-bold bg-emerald-600 hover:bg-emerald-700 text-white shadow-2xs text-xs"
            >
              <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Release from Hold
            </Button>
          )}

          {isSyncSapAvailable && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsSapModalOpen(true)}
              className="rounded-xl font-bold border-purple-500/30 bg-purple-500/5 text-purple-700 hover:bg-purple-500/10 text-xs"
            >
              <Database className="h-3.5 w-3.5 mr-1.5" /> Post SAP MIGO (101)
            </Button>
          )}

          {(grn.status === 'ACCEPTED' || grn.status === 'PARTIALLY_ACCEPTED') && (
            <Button
              size="sm"
              onClick={() =>
                navigate(
                  `${ROUTES.INVOICES.LIST}?create=true&poId=${encodeURIComponent(grn.poId)}&grnId=${encodeURIComponent(
                    grn.id
                  )}&poNumber=${encodeURIComponent(grn.poNumber)}&grnNumber=${encodeURIComponent(
                    grn.grnNumber
                  )}&vendorId=${encodeURIComponent(grn.vendorId)}`
                )
              }
              className="rounded-xl font-bold bg-primary hover:bg-primary/90 text-white shadow-2xs text-xs cursor-pointer"
            >
              <Receipt className="h-3.5 w-3.5 mr-1.5" /> Create Invoice (3-Way Match) →
            </Button>
          )}

          {isCancelAvailable && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancelGrn}
              className="rounded-xl font-bold text-rose-600 hover:bg-rose-50 text-xs"
            >
              <XCircle className="h-3.5 w-3.5 mr-1" /> Cancel GRN
            </Button>
          )}
        </div>
      </div>

      {/* Main Command Center Header Card */}
      <Card className="rounded-2xl border-border bg-card shadow-2xs overflow-hidden">
        <CardContent className="p-6 space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-border/60 pb-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-primary">
                  Material Inwarding & Physical Receipt
                </span>
                <span
                  className={`text-[10px] font-extrabold uppercase px-2.5 py-0.5 rounded-full border ${
                    grn.status === 'ACCEPTED'
                      ? 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20'
                      : grn.status === 'REJECTED' || grn.status === 'CANCELLED'
                      ? 'bg-rose-500/10 text-rose-700 border-rose-500/20'
                      : 'bg-blue-500/10 text-blue-700 border-blue-500/20'
                  }`}
                >
                  {grn.status}
                </span>
              </div>
              <h2 className="text-xl font-black text-foreground">{grn.vendorName}</h2>
              <p className="text-xs text-foreground-secondary">
                Vendor Code: <span className="font-mono font-bold text-foreground">{grn.vendorCode}</span> | Plant: {grn.plantCode} ({grn.plantName})
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="p-3 rounded-xl bg-surface border border-border/70 text-right">
                <span className="text-[10px] font-bold text-foreground-secondary uppercase block">Physical Received</span>
                <span className="text-xl font-black text-foreground font-mono">{grn.totalReceivedQuantity} units</span>
                <span className="text-[10px] text-emerald-600 font-bold block">{grn.totalAcceptedQuantity} Accepted</span>
              </div>

              {grn.totalRejectedQuantity > 0 && (
                <div className="p-3 rounded-xl bg-rose-500/5 border border-rose-500/20 text-right">
                  <span className="text-[10px] font-bold text-rose-600 uppercase block">Rejected Units</span>
                  <span className="text-xl font-black text-rose-600 font-mono">{grn.totalRejectedQuantity} units</span>
                  <span className="text-[10px] text-rose-600/80 font-bold block">Rate: {grn.rejectionRatePercent}%</span>
                </div>
              )}
            </div>
          </div>

          {/* Traceability Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div
              onClick={() => navigate(ROUTES.PURCHASE_ORDERS.DETAILS(grn.poId))}
              className="p-3 rounded-xl bg-surface hover:bg-surface/80 border border-border cursor-pointer transition-colors space-y-1"
            >
              <span className="text-[10px] font-bold text-foreground-muted uppercase block flex items-center justify-between">
                <span>1. Authorized PO</span>
                <ExternalLink className="h-3 w-3" />
              </span>
              <span className="font-bold text-primary block">{grn.poNumber}</span>
              <span className="text-[10px] text-emerald-600 font-semibold">Committed Contract</span>
            </div>

            <div className="p-3 rounded-xl bg-surface border border-border space-y-1">
              <span className="text-[10px] font-bold text-foreground-muted uppercase block">
                2. Delivery Challan
              </span>
              <span className="font-bold text-foreground font-mono block">{grn.deliveryChallanNumber}</span>
              <span className="text-[10px] text-blue-600 font-semibold">{grn.carrierName || 'Freight Carrier'}</span>
            </div>

            <div className="p-3 rounded-xl bg-surface border border-border space-y-1">
              <span className="text-[10px] font-bold text-foreground-muted uppercase block">
                3. Quality Inspection
              </span>
              <span className="font-bold text-foreground block">{grn.qualityStatus}</span>
              <span className="text-[10px] text-purple-600 font-semibold">By {grn.inspectorName}</span>
            </div>

            <div className="p-3 rounded-xl bg-surface border border-border space-y-1">
              <span className="text-[10px] font-bold text-foreground-muted uppercase block">
                4. 3-Way Match Readiness
              </span>
              <span className="font-bold text-foreground block">{grn.threeWayMatchStatus}</span>
              <span className="text-[10px] text-teal-600 font-semibold">Downstream Invoice Input</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Navigation Tabs */}
      <div className="flex items-center gap-1 border-b border-border overflow-x-auto pb-1 text-xs">
        {(
          [
            { id: 'OVERVIEW', label: 'Overview & Dock', icon: FileText },
            { id: 'ITEMS', label: `Line Items (${grn.items.length})`, icon: PackageCheck },
            { id: 'QUALITY', label: 'Quality & Rejection', icon: ShieldCheck },
            { id: 'MATCH', label: '3-Way Match', icon: Layers },
            { id: 'SAP', label: 'SAP S/4HANA MIGO', icon: Database },
            { id: 'DOCUMENTS', label: `Documents (${documents.length})`, icon: FileText },
            { id: 'COMMENTS', label: `Comments (${comments.length})`, icon: MessageSquare },
            { id: 'AUDIT', label: 'Audit Trail', icon: History },
          ] as const
        ).map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;

          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-xl whitespace-nowrap transition-all ${
                isActive
                  ? 'bg-primary/10 text-primary font-black border border-primary/20 shadow-2xs'
                  : 'text-foreground-secondary hover:text-foreground font-bold hover:bg-surface'
              }`}
            >
              <Icon className="h-4 w-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content 1: OVERVIEW */}
      {activeTab === 'OVERVIEW' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="md:col-span-2 rounded-2xl border-border">
            <CardContent className="p-6 space-y-6">
              <div className="border-b border-border/60 pb-3">
                <h3 className="text-sm font-extrabold text-foreground">Inwarding & Logistics Summary</h3>
                <p className="text-xs text-foreground-secondary">
                  Dock receiving milestones, transport vehicle info, and warehouse location assignment.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                <div className="space-y-1">
                  <span className="text-foreground-secondary font-medium">Receipt Date & Time:</span>
                  <p className="font-extrabold text-foreground">{formatDate(grn.receivedDate)}</p>
                </div>

                <div className="space-y-1">
                  <span className="text-foreground-secondary font-medium">Receiving Officer:</span>
                  <p className="font-extrabold text-foreground">{grn.receiverName}</p>
                </div>

                <div className="space-y-1">
                  <span className="text-foreground-secondary font-medium">Delivery Challan Number:</span>
                  <p className="font-mono font-bold text-foreground">{grn.deliveryChallanNumber}</p>
                </div>

                <div className="space-y-1">
                  <span className="text-foreground-secondary font-medium">Logistics Carrier:</span>
                  <p className="font-bold text-foreground">{grn.carrierName || 'Third Party Logistics'}</p>
                </div>

                <div className="space-y-1">
                  <span className="text-foreground-secondary font-medium">Air Waybill / Tracking #:</span>
                  <p className="font-mono text-foreground font-bold">{grn.trackingNumber || 'N/A'}</p>
                </div>

                <div className="space-y-1">
                  <span className="text-foreground-secondary font-medium">Unloading Bay / Dock:</span>
                  <p className="font-bold text-foreground">{grn.unloadingDock || 'Bay-1'}</p>
                </div>

                <div className="sm:col-span-2 space-y-1 pt-2 border-t border-border/40">
                  <span className="text-foreground-secondary font-medium">Receiver Remarks & Condition:</span>
                  <p className="text-foreground font-medium bg-surface p-3 rounded-xl border border-border/60">
                    {grn.notes || 'No damaged packaging noted on primary truck arrival.'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card className="rounded-2xl border-border">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-sm font-extrabold text-foreground flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-primary" /> Warehouse Quality Verdict
                </h3>
                <p className="text-xs text-foreground-secondary">
                  Inspected by {grn.inspectorName} on {formatDate(grn.inspectionDate || grn.receivedDate)}.
                </p>
                <div className="p-3 rounded-xl bg-surface border border-border space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-foreground-secondary">Inspection Status:</span>
                    <span className="font-bold text-foreground">{grn.qualityStatus}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-foreground-secondary">Rejection Rate:</span>
                    <span className="font-bold text-rose-600">{grn.rejectionRatePercent}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-foreground-secondary">Stock Usability:</span>
                    <span className="font-bold text-emerald-600">
                      {grn.totalAcceptedQuantity > 0 ? 'Released to Inventory' : 'Quarantined'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-border">
              <CardContent className="p-6 space-y-3 text-xs">
                <h3 className="text-sm font-extrabold text-foreground flex items-center gap-2">
                  <Database className="h-4 w-4 text-purple-600" /> ERP Ledger Integration
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-foreground-secondary">SAP Movement Type:</span>
                    <span className="font-mono font-bold text-purple-700">101 (Standard GR)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-foreground-secondary">SAP MIGO Voucher:</span>
                    <span className="font-mono font-bold text-foreground">{grn.sapGrnNumber || 'Pending Posting'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-foreground-secondary">Posting Status:</span>
                    <span className="font-bold text-foreground">{grn.sapSyncStatus}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Tab Content 2: ITEMS */}
      {activeTab === 'ITEMS' && (
        <Card className="rounded-2xl border-border">
          <CardContent className="p-6 space-y-4">
            <div className="border-b border-border/60 pb-3">
              <h3 className="text-sm font-extrabold text-foreground">Received Line Items & Stock Allocations</h3>
              <p className="text-xs text-foreground-secondary">
                Detailed verification of material SKUs, batch tracking, received quantities, and storage locations.
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse font-heading">
                <thead>
                  <tr className="border-b border-border/80 text-[10px] font-extrabold text-foreground-secondary uppercase">
                    <th className="py-2.5 px-3">#</th>
                    <th className="py-2.5 px-3">Material / SKU</th>
                    <th className="py-2.5 px-3 text-right">Ordered</th>
                    <th className="py-2.5 px-3 text-right">Received</th>
                    <th className="py-2.5 px-3 text-right text-rose-600">Rejected</th>
                    <th className="py-2.5 px-3 text-right text-emerald-700">Accepted</th>
                    <th className="py-2.5 px-3 text-right text-amber-600">Pending PO</th>
                    <th className="py-2.5 px-3">UOM</th>
                    <th className="py-2.5 px-3">Batch #</th>
                    <th className="py-2.5 px-3">SLoc</th>
                    <th className="py-2.5 px-3">Quality</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/40">
                  {grn.items.map((it, idx) => (
                    <tr key={it.id || idx} className="hover:bg-surface/50 transition-colors">
                      <td className="py-3 px-3 font-mono text-slate-400">{it.lineNumber}</td>
                      <td className="py-3 px-3">
                        <span className="font-bold text-foreground block">{it.description}</span>
                        <span className="text-[10px] text-foreground-muted font-mono">Code: {it.materialCode}</span>
                      </td>
                      <td className="py-3 px-3 text-right font-mono text-foreground-secondary">{it.orderedQuantity}</td>
                      <td className="py-3 px-3 text-right font-mono font-bold text-foreground">{it.receivedQuantity}</td>
                      <td className="py-3 px-3 text-right font-mono font-bold text-rose-600">{it.rejectedQuantity}</td>
                      <td className="py-3 px-3 text-right font-mono font-black text-emerald-700">{it.acceptedQuantity}</td>
                      <td className="py-3 px-3 text-right font-mono font-bold text-amber-600">{it.pendingQuantity}</td>
                      <td className="py-3 px-3 text-foreground-secondary">{it.uom}</td>
                      <td className="py-3 px-3 font-mono text-foreground-secondary">{it.batchNumber || 'N/A'}</td>
                      <td className="py-3 px-3 font-mono text-primary font-bold">{it.storageLocation}</td>
                      <td className="py-3 px-3">
                        <span
                          className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full ${
                            it.qualityStatus === 'PASSED'
                              ? 'bg-emerald-500/10 text-emerald-700'
                              : it.qualityStatus === 'FAILED'
                              ? 'bg-rose-500/10 text-rose-700'
                              : 'bg-amber-500/10 text-amber-700'
                          }`}
                        >
                          {it.qualityStatus}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tab Content 3: QUALITY */}
      {activeTab === 'QUALITY' && (
        <Card className="rounded-2xl border-border">
          <CardContent className="p-6 space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-border/60 pb-3">
              <div>
                <h3 className="text-sm font-extrabold text-foreground flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-primary" /> Warehouse Quality Inspection Breakdown
                </h3>
                <p className="text-xs text-foreground-secondary">
                  Verification of physical tolerances, damage reporting, and vendor defect reasons.
                </p>
              </div>

              <Button
                size="sm"
                onClick={() => setIsInspectionModalOpen(true)}
                className="rounded-xl font-bold bg-primary text-white text-xs"
              >
                <ShieldCheck className="h-3.5 w-3.5 mr-1.5" /> Re-Inspect / Edit
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 text-xs">
              <div className="p-4 rounded-xl border border-border bg-surface space-y-1">
                <span className="text-[10px] font-bold text-foreground-secondary uppercase">Inspected Units</span>
                <p className="font-mono text-xl font-black text-foreground">{grn.totalReceivedQuantity}</p>
                <span className="text-[10px] text-foreground-muted">100% Inward Sampling</span>
              </div>

              <div className="p-4 rounded-xl border border-emerald-500/20 bg-emerald-500/5 space-y-1">
                <span className="text-[10px] font-bold text-emerald-700 uppercase">Accepted Units</span>
                <p className="font-mono text-xl font-black text-emerald-600">{grn.totalAcceptedQuantity}</p>
                <span className="text-[10px] text-emerald-700/80">Stock Cleared</span>
              </div>

              <div className="p-4 rounded-xl border border-rose-500/20 bg-rose-500/5 space-y-1">
                <span className="text-[10px] font-bold text-rose-700 uppercase">Rejected Units</span>
                <p className="font-mono text-xl font-black text-rose-600">{grn.totalRejectedQuantity}</p>
                <span className="text-[10px] text-rose-700/80">Return to Vendor</span>
              </div>

              <div className="p-4 rounded-xl border border-border bg-surface space-y-1">
                <span className="text-[10px] font-bold text-foreground-secondary uppercase">Rejection Rate</span>
                <p className="font-mono text-xl font-black text-primary">{grn.rejectionRatePercent}%</p>
                <span className="text-[10px] text-foreground-muted">Tolerance: 2.0%</span>
              </div>
            </div>

            {/* Rejection Justification Table */}
            {grn.totalRejectedQuantity > 0 ? (
              <div className="space-y-3 pt-2">
                <h4 className="text-xs font-black uppercase text-foreground">Identified Defect Observations</h4>
                <div className="divide-y divide-border/60 border border-border rounded-xl overflow-hidden text-xs">
                  {grn.items
                    .filter((it) => it.rejectedQuantity > 0)
                    .map((it) => (
                      <div key={it.id} className="p-4 bg-card flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="space-y-1">
                          <span className="font-bold text-foreground">{it.description}</span>
                          <span className="text-[10px] text-foreground-muted font-mono block">SKU: {it.materialCode}</span>
                          <p className="text-foreground-secondary text-xs">{it.rejectionNotes || 'Defect recorded during inward testing.'}</p>
                        </div>
                        <div className="text-right">
                          <span className="text-xs font-black text-rose-600 font-mono block">
                            {it.rejectedQuantity} units rejected
                          </span>
                          <span className="text-[10px] font-bold text-foreground-secondary bg-surface px-2 py-0.5 rounded border border-border">
                            Reason: {it.rejectionReason?.replace(/_/g, ' ') || 'Defect'}
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            ) : (
              <div className="p-6 text-center text-xs text-emerald-700 font-bold bg-emerald-500/5 rounded-xl border border-emerald-500/20">
                Zero quality defects or damaged packaging observed during receiving. All units accepted into plant stock.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tab Content 4: MATCH */}
      {activeTab === 'MATCH' && (
        <Card className="rounded-2xl border-border">
          <CardContent className="p-6 space-y-6">
            <div className="border-b border-border/60 pb-3">
              <h3 className="text-sm font-extrabold text-foreground flex items-center gap-2">
                <Layers className="h-4 w-4 text-purple-600" /> 3-Way Reconciliation Audit (PO vs GRN vs Invoice)
              </h3>
              <p className="text-xs text-foreground-secondary">
                Commercial and physical quantity matching prior to invoice payment release.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs font-heading">
              <div className="p-4 rounded-xl border border-primary/20 bg-primary/5 space-y-1.5">
                <span className="text-[10px] font-extrabold text-primary uppercase block">1. Purchase Order Authorized</span>
                <span className="font-mono text-xl font-black text-foreground block">
                  {grn.totalOrderedQuantity} units
                </span>
                <span className="text-[10px] text-foreground-muted block font-mono">PO #{grn.poNumber}</span>
              </div>

              <div className="p-4 rounded-xl border border-emerald-500/20 bg-emerald-500/5 space-y-1.5">
                <span className="text-[10px] font-extrabold text-emerald-700 uppercase block">2. GRN Accepted Stock</span>
                <span className="font-mono text-xl font-black text-emerald-600 block">
                  {grn.totalAcceptedQuantity} units
                </span>
                <span className="text-[10px] text-foreground-muted block font-mono">GRN #{grn.grnNumber}</span>
              </div>

              <div className="p-4 rounded-xl border border-purple-500/20 bg-purple-500/5 space-y-1.5">
                <span className="text-[10px] font-extrabold text-purple-700 uppercase block">3. Invoice Billed Units</span>
                <span className="font-mono text-xl font-black text-purple-600 block">
                  {grn.totalAcceptedQuantity} units
                </span>
                <span className="text-[10px] text-foreground-muted block font-mono">Invoice #{grn.invoiceNumber || 'Pending'}</span>
              </div>
            </div>

            <div className="p-4 rounded-xl border border-border bg-surface text-xs space-y-2">
              <span className="font-bold text-foreground block">Match Verdict:</span>
              <p className="text-foreground-secondary leading-relaxed">
                {grn.totalRejectedQuantity > 0
                  ? `Quantity variance detected: ${grn.totalRejectedQuantity} units were rejected during receiving and are barred from invoice payment authorization.`
                  : 'Quantities reconcile 100% with authorized PO commitments. Clean 3-way match verified.'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tab Content 5: SAP */}
      {activeTab === 'SAP' && (
        <Card className="rounded-2xl border-border">
          <CardContent className="p-6 space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border/60 pb-3">
              <div>
                <h3 className="text-sm font-extrabold text-foreground flex items-center gap-2">
                  <Database className="h-4 w-4 text-purple-600" /> SAP S/4HANA MIGO Goods Receipt Simulator
                </h3>
                <p className="text-xs text-foreground-secondary">
                  Bi-directional replication of warehouse Goods Receipt Note into SAP ERP ledger via simulated BAPI `BAPI_GOODSMVT_CREATE`.
                </p>
              </div>

              {grn.sapSyncStatus !== 'SYNCED' && (
                <Button
                  size="sm"
                  onClick={() => setIsSapModalOpen(true)}
                  className="rounded-xl font-bold bg-purple-600 hover:bg-purple-700 text-white shadow-2xs text-xs"
                >
                  <Database className="h-3.5 w-3.5 mr-1.5" /> Post to SAP MIGO
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
              <div className="p-4 rounded-xl border border-border bg-surface space-y-1">
                <span className="text-foreground-secondary font-bold uppercase text-[10px]">SAP Document Number</span>
                <p className="font-mono text-base font-black text-purple-700">{grn.sapGrnNumber || 'PENDING GENERATION'}</p>
                <span className="text-[10px] text-foreground-muted">Standard 10-digit SAP material voucher</span>
              </div>

              <div className="p-4 rounded-xl border border-border bg-surface space-y-1">
                <span className="text-foreground-secondary font-bold uppercase text-[10px]">Movement Type</span>
                <p className="font-mono text-base font-black text-foreground">101 — GR for PO</p>
                <span className="text-[10px] text-foreground-muted">Standard warehouse inventory receipt</span>
              </div>

              <div className="p-4 rounded-xl border border-border bg-surface space-y-1">
                <span className="text-foreground-secondary font-bold uppercase text-[10px]">SAP Target Plant</span>
                <p className="font-mono text-base font-black text-foreground">{grn.plantCode}</p>
                <span className="text-[10px] text-foreground-muted">Storage Location: {grn.storageLocation}</span>
              </div>
            </div>

            <div className="p-4 rounded-xl border border-border/80 bg-slate-900 text-slate-100 font-mono text-xs overflow-x-auto space-y-2">
              <span className="text-slate-400 text-[10px] uppercase font-bold block">// BAPI_GOODSMVT_CREATE Payload Schema</span>
              <pre className="text-[11px] leading-relaxed">
{JSON.stringify(
  {
    HEADER: {
      PSTNG_DATE: grn.receivedDate,
      DOC_DATE: grn.receivedDate,
      REF_DOC_NO: grn.deliveryChallanNumber,
      PR_UNAME: grn.receiverName,
    },
    ITEMS: grn.items.map((it) => ({
      PO_NUMBER: grn.poNumber,
      PO_ITEM: it.lineNumber,
      MATERIAL: it.materialCode,
      QUANTITY: it.acceptedQuantity,
      UOM: it.uom,
      PLANT: grn.plantCode,
      STGE_LOC: it.storageLocation,
      MOVE_TYPE: '101',
    })),
  },
  null,
  2
)}
              </pre>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tab Content 6: DOCUMENTS */}
      {activeTab === 'DOCUMENTS' && (
        <DocumentManagementPanel
          documents={documents}
          onUploadFile={async (file, category) => {
            if (!grn) return;
            const newDoc = await mockDocumentCommentService.uploadDocument('GRN', grn.id, file, category);
            setDocuments((prev) => [newDoc, ...prev]);
          }}
          onDownloadFile={async (docId) => {
            await mockDocumentCommentService.downloadDocument(docId);
          }}
          onDeleteFile={async (docId) => {
            await mockDocumentCommentService.deleteDocument(docId);
            setDocuments((prev) => prev.filter((d) => d.id !== docId));
          }}
        />
      )}

      {/* Tab Content 7: COMMENTS */}
      {activeTab === 'COMMENTS' && (
        <EnterpriseCommentThread
          comments={comments}
          onAddComment={async (text, attachmentIds) => {
            if (!grn) return;
            const newComment = await mockDocumentCommentService.createComment(
              'GRN',
              grn.id,
              text,
              attachmentIds
            );
            setComments((prev) => [...prev, newComment]);
          }}
          onDeleteComment={async (commentId) => {
            await mockDocumentCommentService.deleteComment(commentId);
            setComments((prev) => prev.filter((c) => c.id !== commentId));
          }}
        />
      )}

      {/* Tab Content 8: AUDIT */}
      {activeTab === 'AUDIT' && <GrnAuditHistoryTab auditHistory={grn.auditHistory} />}

      {/* Modals */}
      <GrnInspectionModal
        isOpen={isInspectionModalOpen}
        onClose={() => setIsInspectionModalOpen(false)}
        grn={grn}
        onConfirm={async (payload) => {
          await mockGoodsReceiptService.recordQualityInspection(
            grn.id,
            payload.itemInspections,
            payload.inspectorNotes
          );
          await loadData();
        }}
      />

      <GrnPrintSlipModal
        isOpen={isPrintModalOpen}
        onClose={() => setIsPrintModalOpen(false)}
        grn={grn}
      />

      <GrnSapSyncModal
        isOpen={isSapModalOpen}
        onClose={() => setIsSapModalOpen(false)}
        onConfirmSync={handleConfirmSapSync}
        grn={grn}
        isSubmitting={isSyncingSap}
      />
    </div>
  );
};
